import _ from "lodash";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as RS from "react-select";
import { AuthorSortModal } from "./AuthorSortModal";
import * as Comp from "../../../../components";
import { IComponentWithValidatedForm } from "../../../../components/validation";
import { AllowedImageUploadExtns } from "../../../../constants";
import { CoAuthorForm } from "../../../../containers/abstracts/composer/components";
import { Statuses } from "../../../../domain";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api } from "../../../../services/Api";
import { LegacyApi } from "../../../../services/LegacyApi";
import { ReactHelper, Utility } from "../../../../utils";

interface OwnProps {
    api: Api;
    formComponent: IComponentWithValidatedForm;
}

interface OwnState {
    changePhoto: { [index: number ]: boolean; };
    isManual: boolean;
    search: string;
    searching: boolean;
    searchOpen: boolean;
    suggestions: M.Member.BasicProfile[];
}

export class CoAuthorsEditor extends React.Component<OwnProps, OwnState> {

    private _searchTimeout: NodeJS.Timer;
    private _sortModal: AuthorSortModal | null;

    public constructor(props: any) {
        super(props);
        this.state = {
            changePhoto: {},
            isManual: false,
            search: "",
            searching: false,
            searchOpen: false,
            suggestions: [],
        };
    }

    public render() {
        const formModel = this.props.formComponent.getFormModelData<Forms.AdminAbstract.AbstractAuthorshipData>();
        const roles: M.Editors.SelectItem[] = M.Abstract.AbstractAuthorRoles.Values.map((x) => ({
            id: x.id,
            render: x.name,
        }));

        const activeAuthors = _.orderBy(formModel.authors, "sort").filter((x) => !x.deleted);
        const authors = activeAuthors.map((x, i) => {
                const isPrimary = M.Abstract.AbstractAuthorRoles.isPrimary(x);

                return <li key={i} className="list-builder-item">
                    <div className="list-builder-fields">
                        <div className="authors">
                            <ul>
                                <li>
                                    <Comp.Reusable.PersonSearchListItem
                                        avatar={M.Generic.PersonHelper.getAvatarUrl(x)}
                                        email={x.email}
                                        name={M.Generic.PersonHelper.getFullName(x)}
                                        title={x.title}
                                    />
                                </li>
                            </ul>
                        </div>

                        <Comp.Editors.DropdownField
                            formComponent={this.props.formComponent}
                            name={`authors[${x.index}].roleId`}
                            compact
                            items={roles}
                            placeholder="Select role..."
                        />

                        {isPrimary
                            ? <Comp.Editors.PhoneNumberField
                                formComponent={this.props.formComponent}
                                name={`authors[${x.index}].phone`}
                                compact
                                hideType
                                required
                            />
                            : null}

                        {isPrimary && x.userId > 0
                            ? <>
                                    <p>
                                        <label>
                                            <input
                                                type="checkbox"
                                                name={`authors[${x.index}].displayProfilePicture`}
                                                checked={x.displayProfilePicture}
                                                onChange={() => {}}
                                            />{" "}
                                            Display profile photo
                                        </label>
                                    </p>

                                    <p>
                                        <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleChangeProfilePhoto(x.id); }}>Change profile photo</a>
                                    </p>

                                    <BS.Collapse in={!!this.state.changePhoto[x.id]}>
                                        <div className="highlight">
                                            <h3>Choose a new profile photo</h3>

                                            <p className="mute">Allowed file extension(s): {Utility.joinSkipEmpty(AllowedImageUploadExtns, ", ")}</p>

                                            <Comp.Upload.FileUploader
                                                buttons={<BS.Button bsStyle="link" onClick={() => this.toggleChangeProfilePhoto(x.id)}>Cancel</BS.Button>}
                                                uploadOnSelect
                                                uploader={{
                                                    filters: {
                                                        max_file_size: "10mb",
                                                        mime_types: [
                                                            { title: "Images", extensions: Utility.joinSkipEmpty(AllowedImageUploadExtns, ",") },
                                                        ],
                                                        prevent_duplicates: true,
                                                    },
                                                    multipart_params: {
                                                        userId: x.userId,
                                                    },
                                                    url: Api.endpoints.admin.user.profilePicture.absoluteApiUrl(),
                                                }}
                                                onUploadComplete={(media) => this.onProfilePictureUploaded(x.id, x.legacyMemberId || 0, media)}
                                            />
                                        </div>
                                    </BS.Collapse>
                                </>
                            : null}
                    </div>
                    <a className="req remove-trigger" href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.onDelete(x.index); }} title="Remove author">
                        <Comp.Generic.Icon icon="times" />
                    </a>
                </li>;
            });

        const saveCancelBtns = this.state.isManual
            ? null
            : <p>
                <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleSearchBox(); }}>Cancel</a>
            </p>;

        const errors = this.props.formComponent.getFieldErrors("authors");

        return <Comp.Generic.WrapperLess>
            <AuthorSortModal
                form={this.props.formComponent}
                ref={(ref) => this._sortModal = ref}
            />

            <Comp.Editors.FormField name="" label="Authors" errors={errors}>
                <div style={{ display: authors.length ? "block" : "none" }}>
                    <ul className="list-builder">
                        {authors}
                    </ul>
                </div>

                <input type="hidden" name="authors" />
            </Comp.Editors.FormField>

            <Comp.Editors.FormField name="">
                <BS.ButtonGroup>
                    <BS.Button bsStyle="default" onClick={() => this.toggleSearchBox()}>
                        <Comp.Generic.Icon icon="plus" /> Add an author
                    </BS.Button>
                    {activeAuthors.length > 1
                        ? <BS.Button bsStyle="default" onClick={() => this._sortModal!.show()}>
                            Sort authors
                        </BS.Button>
                        : null}
                </BS.ButtonGroup>

                <BS.Collapse in={this.state.searchOpen}>
                    <div className="highlight coauthor-form">
                        <h3>Add an author</h3>

                        <RS.Async
                            arrowRenderer={(arrowProps) => <button className="search-button" type="button"><Comp.Generic.Icon icon="search" /></button>}
                            className="authors select-single"
                            filterOptions={(options) => options}
                            ignoreCase
                            loadOptions={(search: string) => this.search(search)}
                            searchPromptText="Start typing and select from suggested"
                            onChange={(user: M.Member.BasicProfile) => { this.onAdd(user); this.toggleSearchBox(); }}
                            optionRenderer={(user: M.Member.BasicProfile) => {
                                return <li>
                                    <Comp.Reusable.PersonSearchListItem
                                        avatar={M.Generic.PersonHelper.getAvatarUrl(user)}
                                        email={user.email || ""}
                                        name={M.Generic.PersonHelper.getFullName(user)}
                                        title={user.degrees}
                                    />
                                </li>;
                            }}
                            placeholder="Search..."
                        />

                        <p>
                            <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleManualSearch(); }}>
                                Enter name manually &raquo;
                            </a>
                        </p>

                        <CoAuthorForm
                            api={this.props.api}
                            isAdmin={true}
                            show={this.state.isManual}
                            onCancel={() => this.toggleManualSearch()}
                            onSave={(author) => this.handleAuthorSave(author)}
                        />

                        {saveCancelBtns}
                    </div>
                </BS.Collapse>
            </Comp.Editors.FormField>
        </Comp.Generic.WrapperLess>;
    }

    private onAdd(user: M.Member.BasicProfile) {
        if (!user)
            return;

        const formModel = this.props.formComponent.getFormModelData<Forms.AdminAbstract.AbstractAuthorshipData>();
        const existingAuthor = _.find(formModel.authors, (c) => !c.deleted
            && ((c.userId > 0 && c.userId === user.userId)
                || (c.firstName === user.firstName
                    && c.lastName === user.lastName
                    && c.email === user.email)));

        if (!existingAuthor) {
            formModel.authors.push({
                deleted: false,
                displayProfilePicture: false,
                email: user.email || "",
                id: 0,
                index: formModel.authors.length,
                isMember: user.isMember,
                isNew: true,
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                legacyMemberId: user.legacyMemberId,
                phone: { ...Forms.Generic.DefaultPhoneNumber },
                profilePicture: user.profilePicture || "",
                roleId: 0,
                sort: (_.max(formModel.authors.map((x) => x.sort)) || 0) + 1,
                title: user.degrees || "",
                userId: user.userId,
            });

            this.props.formComponent.updateFormModelData(formModel);
        }
    }

    private onDelete(index: number) {
        const formModel = this.props.formComponent.getFormModelData<Forms.AdminAbstract.AbstractAuthorshipData>();
        const authors = _.find(formModel.authors, (c) => c.index === index);

        if (authors) {
            authors.deleted = true;
            this.props.formComponent.updateFormModelData(formModel);
        }
    }

    private onProfilePictureUploaded(userId: number, legacyMemberId: number, media: M.Upload.Media) {
        if (!media.url) {
            alert("Failed to upload profile picture");
            return;
        }

        // try load image
        const img = new Image();
        img.onload = () => {
            const formModel = this.props.formComponent.getFormModelData<Forms.AdminAbstract.AbstractAuthorshipData>();
            const author = _.find(formModel.authors, (x) => x.id === userId);
            if (author) {
                author.profilePicture = media.url;
                const changePhoto = { ...this.state.changePhoto };
                changePhoto[userId] = false;
                this.setState({ ...this.state, changePhoto }, () => {
                    this.props.formComponent.updateFormModelData(formModel);
                    // update legacy member profile picture
                    const legacyApi = new LegacyApi();
                    legacyApi.updateMemberProfilePicture(media.url || "", legacyMemberId).then();
                });
            }
        };
        img.onerror = (err) => console.error("Failed to load", media, err);
        img.src = media.url;
    }

    private handleAuthorSave(author: Forms.Abstract.ComposeNewCoAuthorData) {
        const user: M.Member.BasicProfile = {
            degrees: author.title || "",
            email: author.email || null,
            firstName: author.firstName || "",
            id: 0,
            lastName: author.lastName || "",
            memberId: 0,
            isMember: false,
            statusId: Statuses.Active.id,
            userId: 0,
            username: author.email || "",
        };

        this.setState({
            ...this.state,
            isManual: false,
            searchOpen: false,
        }, () => {
            this.onAdd(user);
        });
    }

    private search(query: string) {
        if (!query || query.length < 2)
            return Promise.resolve({ options: [] });

        return new Promise((resolve, reject) => {
            // delay the ajax call as to not make a call for each letter typed.
            if (this._searchTimeout !== undefined)
                clearTimeout(this._searchTimeout);

            this._searchTimeout = setTimeout(() => {
                const queryParams = { queryParams: { query, includeNonMembers: true, max: 25 }};

                this.props.api.callApi(Api.endpoints.members.search, queryParams)
                    .then((result) => {
                        if (result.isSuccess && result.data) {
                            let options = result.data.map((x) => {
                                return Utility.toReactSelectOption(x, M.Generic.PersonHelper.getFullName(x), x.userId);
                            });

                            options = [...options];
                            resolve({ options });
                        }
                        else
                            console.error(result.errors.join("; "));
                    });
            }, 375);
        }).then((data) => {
            return data;
        });
    }

    private toggleManualSearch() {
        this.setState({ ...this.state, isManual: !this.state.isManual });
    }

    private toggleChangeProfilePhoto(id: number) {
        const changePhoto = { ...this.state.changePhoto };
        changePhoto[id] = !!!changePhoto[id];
        this.setState({ ...this.state, changePhoto });
    }

    private toggleSearchBox() {
        this.setState({ ...this.state, searchOpen: !this.state.searchOpen }, () => {
            if (!this.state.searchOpen)
                this.setState({ ...this.state, isManual: false });
        });
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/edit/components/CoAuthorsEditor.tsx